/**
 * The key to the diagram's shapes, in the pane's edge chrome.
 *
 * Round 7, owner: "There needs to be an explanation of what different shapes
 * in the diagrams represent." The guide draws a fixed vocabulary (round 4,
 * and the system prompt states it verbatim), so a reader can learn it once
 * and read every picture after that. Nothing on screen taught it.
 *
 * Why it is a button and not a panel: the guide is BANNED from drawing a
 * legend into the mermaid itself ("never draw a legend: a picture that needs
 * a key is a picture the reader cannot read"), and the same argument applies
 * to a permanent panel in the pane. A key is something you consult once and
 * then stop seeing. So it is one small mark that opens a card and closes
 * again: Escape, a click outside, or the button itself.
 *
 * Where it lives: the boundary rail (SessionPage), which is reserved layout
 * space beside the drawing rather than an overlay ON it. That is the same
 * fix as the collapse tab's, for the same reason: nothing in the pane chrome
 * may cover a node.
 *
 * The card opens AWAY from the transcript, over the drawing it explains, and
 * is absolutely placed, so opening it moves nothing.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { TranscriptSide } from '../../stores/uiStore';

/** One entry in the key: the mark the guide draws, and what it means. */
export interface DiagramShape {
  /** the mermaid shape name, which is also the row's stable id */
  key: string;
  /** what that shape stands for, in the product's words */
  name: string;
  glyph: ReactNode;
}

/** The frame every glyph is drawn in: one line weight, one size, no fill. */
function ShapeGlyph({ children }: { children: ReactNode }) {
  return (
    <svg
      width={30}
      height={17}
      viewBox="0 0 32 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinejoin="round"
      className="shrink-0 text-secondary"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/**
 * The vocabulary, in the order a reader meets it: the things that run, the
 * things that hold data, the things at the edges, then the two marks that are
 * about control rather than about a component.
 *
 * This list IS the contract with the prompt (services/agent/prompt.py: "service
 * =rounded, worker=subproc, database=cyl, queue=h-cyl, file storage=lin-cyl,
 * external SaaS or LLM API=stadium, user/client=circle, decision=diam, cron
 * trigger=hex"). Nine shapes there, nine rows here.
 */
export const DIAGRAM_SHAPES: readonly DiagramShape[] = [
  {
    key: 'rounded',
    name: 'Service',
    glyph: (
      <ShapeGlyph>
        {/* A modest radius, deliberately: at 30px the stadium below is the
            same box with rx = half its height, and two nearly-round marks in
            one key teach nothing. */}
        <rect x="2" y="3" width="28" height="12" rx="2.5" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'subproc',
    name: 'Worker',
    glyph: (
      <ShapeGlyph>
        <rect x="2" y="3" width="28" height="12" />
        <path d="M6 3v12M26 3v12" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'cyl',
    name: 'Database',
    glyph: (
      <ShapeGlyph>
        <path d="M10 5v8c0 1.1 2.7 2 6 2s6-0.9 6-2V5" />
        <ellipse cx="16" cy="5" rx="6" ry="2" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'h-cyl',
    name: 'Queue',
    glyph: (
      <ShapeGlyph>
        {/* The seam sits at the NEAR end and closes into an ellipse with the
            cap beside it: that is what reads as a cylinder lying down. Drawn
            at the far end it reads as a toggle switch. */}
        <path d="M9 4h14a5 5 0 0 1 0 10H9a5 5 0 0 1 0-10z" />
        <path d="M9 4a3 5 0 0 1 0 10" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'lin-cyl',
    name: 'File storage',
    glyph: (
      <ShapeGlyph>
        <path d="M10 5v8c0 1.1 2.7 2 6 2s6-0.9 6-2V5" />
        <ellipse cx="16" cy="5" rx="6" ry="2" />
        <path d="M10 9c0 1.1 2.7 2 6 2s6-0.9 6-2" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'stadium',
    name: 'Outside service or LLM API',
    glyph: (
      <ShapeGlyph>
        <rect x="2" y="3" width="28" height="12" rx="6" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'circle',
    name: 'A person or their browser',
    glyph: (
      <ShapeGlyph>
        <circle cx="16" cy="9" r="6" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'diam',
    name: 'A decision the code makes',
    glyph: (
      <ShapeGlyph>
        <path d="M16 2.5 29.5 9 16 15.5 2.5 9z" />
      </ShapeGlyph>
    ),
  },
  {
    key: 'hex',
    name: 'Something on a schedule',
    glyph: (
      <ShapeGlyph>
        <path d="M9 3.5h14l5 5.5-5 5.5H9L4 9z" />
      </ShapeGlyph>
    ),
  },
];

/** The mark on the button: three of the shapes, small, overlapping nothing. */
function LegendGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2.5" y="3" width="9" height="7" rx="2" />
      <circle cx="18" cy="6.5" r="3.5" />
      <path d="M8 13.5 13 18l-5 4.5L3 18z" />
      <path d="M14.5 15h7M14.5 19h7" />
    </svg>
  );
}

export function ShapeLegend({ side }: { side: TranscriptSide }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // A click anywhere else dismisses it, and deliberately does NOT move focus:
  // the pointer already went where the reader meant it to go.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Escape closes from anywhere and hands focus back, so the key is never a
  // place the keyboard gets stuck.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Opening moves the reader into the card: it is what they asked for, and a
  // screen reader should hear it rather than only hear the button change.
  useEffect(() => {
    if (open) cardRef.current?.focus();
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="shape-legend-button"
        title="What the shapes mean"
        aria-label="What the shapes mean"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded-pill border border-default bg-raised shadow-raised transition-colors duration-(--motion-hover) hover:border-(--accent) hover:text-primary ${
          open ? 'text-primary' : 'text-tertiary'
        }`}
      >
        <LegendGlyph />
      </button>
      {open ? (
        <div
          ref={cardRef}
          role="dialog"
          aria-label="What the shapes mean"
          data-testid="shape-legend"
          data-side={side}
          tabIndex={-1}
          /* Absolutely placed, opening over the drawing it explains, on the
             side away from the transcript. */
          className={`absolute top-1/2 z-40 max-h-[70vh] w-64 -translate-y-1/2 overflow-y-auto rounded-panel border border-default bg-raised p-3 shadow-overlay ${
            side === 'right' ? 'right-full mr-2' : 'left-full ml-2'
          }`}
        >
          <p className="mt-0 mb-2 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] text-tertiary uppercase">
            What the shapes mean
          </p>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {DIAGRAM_SHAPES.map((shape) => (
              <li
                key={shape.key}
                data-testid={`shape-legend-${shape.key}`}
                className="flex items-center gap-2.5 text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-secondary"
              >
                {shape.glyph}
                <span className="min-w-0 flex-1">{shape.name}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2.5 mb-0 border-t border-default pt-2 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
            Every diagram here uses these. The key lives on the edge rather than
            in the picture, so the drawing stays a drawing.
          </p>
        </div>
      ) : null}
    </div>
  );
}
